import React, { useCallback } from 'react';
import { componentWillAppendToBody } from 'react-append-to-body';
import styled, { useTheme } from 'styled-components';

import {
  animations,
  colors,
  maxWidthBreakpoints,
  media,
  useKeypress,
  useMediaQuery,
} from '../../../core';
import Button from '../Button';
import Card from '../Card';
import Heading from '../Heading';
import Icon from '../Icon';

export interface ModalProps {
  children?: React.ReactNode;
  onClose?: () => void;
  title: string;
  width?: string;
  showButtons?: boolean;
  onPress?: () => void;
  submitText?: string;
  cancelText?: string;
}

const Background = styled.div`
  align-items: center;
  background-color: rgba(255, 255, 255, 0.8);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  padding: 16px;
  position: fixed;
  right: 0;
  top: 0;
  width: 100%;
  z-index: 1001;
`;

const Scroll = styled.div`
  overflow-y: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const ModalCard = styled.div<{ width?: string }>`
  animation: ${animations.fadeAndSlideInFromTop} 0.2s ease-out forwards;
  width: ${({ width }) => (width ? width : '760px')};
  max-height: calc(100vh);
  padding: 40px 0;

  ${media.tabletPortrait} {
    width: 90%;
  }
`;

const ButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 0 24px;

  ${media.tabletPortrait} {
    justify-content: space-between;
  }
`;

const StyledButton = styled(Button)`
  margin: 0 20px;

  ${media.tabletPortrait} {
    margin: 0 4px;
    min-width: 40%;
  }
`;

const Header = styled.div<{ showTitle?: boolean }>`
  border-bottom: ${({ showTitle, theme }) =>
    showTitle && `1px solid ${theme.modalLineColor || colors.primary}`};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 24px;
  position: relative;
`;

const StyledHeading = styled(Heading).attrs({
  size: 6,
})`
  line-height: 26px;
  margin-bottom: 4px;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: row;
`;

const StyledIcon = styled(Icon)`
  cursor: pointer;
  margin-left: 20px;
`;

const Container = styled.section`
  margin-bottom: 30px;
  width: 100%;
`;

const Modal = ({
  children,
  onClose,
  showButtons,
  onPress,
  submitText,
  width,
  title,
  cancelText,
}: ModalProps) => {
  const showCloseButton = !!onClose;
  const showTitle = !!title;
  const isXSmallMedia = useMediaQuery(maxWidthBreakpoints.xsmall);

  const theme = useTheme();

  useKeypress('Escape', () => {
    if (onClose) {
      onClose();
    }
  });

  const handleCloseClick = () => {
    if (onClose) {
      onClose();
    }
  };

  const handleBackgroundClick = useCallback(
    (clickEvent) => {
      if (clickEvent.currentTarget !== clickEvent.target) {
        return;
      }
      if (onClose) {
        onClose();
      }
    },
    [onClose]
  );

  return (
    <Background onClick={handleBackgroundClick}>
      <Scroll onClick={handleBackgroundClick}>
        <ModalCard width={width}>
          <Card>
            <Container>
              <Header showTitle={showTitle}>
                <StyledHeading>{showTitle && title}</StyledHeading>
                <Actions>
                  {showCloseButton && (
                    <StyledIcon
                      color={theme.iconColor || colors.primary}
                      type="error"
                      onClick={handleCloseClick}
                    />
                  )}
                </Actions>
              </Header>
              {children}
            </Container>
            {showButtons && (
              <ButtonsWrapper>
                {onClose && (
                  <StyledButton
                    variant="secondary"
                    size={isXSmallMedia ? 'xsmall' : 'medium'}
                    onClick={onClose}
                  >
                    {cancelText ? cancelText : 'Cancel'}
                  </StyledButton>
                )}
                {onPress && (
                  <StyledButton
                    variant="primary"
                    size={isXSmallMedia ? 'xsmall' : 'medium'}
                    onClick={onPress}
                  >
                    {submitText ? submitText : 'OK'}
                  </StyledButton>
                )}
              </ButtonsWrapper>
            )}
          </Card>
        </ModalCard>
      </Scroll>
    </Background>
  );
};

export default componentWillAppendToBody(Modal);
